import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Typography, Row, Col } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { LockOutlined, SafetyOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import '../../styles/colors.css';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [codeError, setCodeError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');
  const codeFromUrl = searchParams.get('code');

  useEffect(() => {
    if (codeFromUrl) {
      setCode(codeFromUrl);
    }
  }, [codeFromUrl]);

  const validateForm = () => {
    let isValid = true;
    setCodeError('');
    setPasswordError('');
    setConfirmPasswordError('');

    if (!code) {
      setCodeError('Doğrulama kodunu girin!');
      isValid = false;
    } else if (code.length !== 6) {
      setCodeError('Doğrulama kodu 6 haneli olmalıdır!');
      isValid = false;
    }

    if (!newPassword) {
      setPasswordError('Yeni şifrenizi girin!');
      isValid = false;
    } else if (newPassword.length < 6) {
      setPasswordError('Şifre en az 6 karakter olmalıdır!');
      isValid = false;
    }

    if (!confirmPassword) {
      setConfirmPasswordError('Şifre tekrarını girin!');
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Şifreler eşleşmiyor!');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!validateForm()) {
      return;
    }

    if (!token) {
      toast.error('Geçersiz şifre sıfırlama linki!');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.resetPassword(token, code, newPassword);
      if (response.success) {
        setSuccess(true);
        toast.success('Şifreniz başarıyla sıfırlandı!');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast.error(response.message || 'Şifre sıfırlama başarısız oldu.');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}>
        <Card style={{ maxWidth: '500px', width: '100%', textAlign: 'center' }}>
          <Title level={3} style={{ marginBottom: '16px', color: 'var(--error)' }}>
            Geçersiz Link
          </Title>
          <Text style={{ color: 'var(--text-secondary)', fontSize: '1rem', marginBottom: '24px', display: 'block' }}>
            Şifre sıfırlama linki geçersiz veya eksik. Lütfen e-postanızdaki linki kontrol edin.
          </Text>
          <Button
            type="primary"
            onClick={() => navigate('/forgot-password')}
            style={{
              width: '100%',
              height: '48px',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Yeni Şifre Sıfırlama Talebi
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex'
    }}>
      <Row style={{ width: '100%', minHeight: '100vh', margin: 0 }}>
        {/* Sol Taraf - Resim */}
        <Col xs={0} lg={12} style={{ 
          background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 2,
            width: '80%',
            maxWidth: '500px'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
              fontSize: '3rem',
              fontWeight: 'bold',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              M
            </div>
            <Title level={1} style={{ 
              color: 'white',
              fontSize: '3rem',
              fontWeight: '700',
              marginBottom: '16px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              Menteem
            </Title>
            <Text style={{ 
              color: 'rgba(255,255,255,0.9)',
              fontSize: '1.3rem',
              fontWeight: '500'
            }}>
              Yeni şifrenizi belirleyin
            </Text>
          </div>
        </Col>

        {/* Sağ Taraf - Form */}
        <Col xs={24} lg={12} style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          background: 'white',
          maxHeight: '100vh',
          overflowY: 'auto'
        }}>
          <div className="auth-form-card" style={{ width: '100%', maxWidth: '400px' }}>
            {!success ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                  <Title level={2} style={{ 
                    marginBottom: '8px',
                    color: 'var(--text-primary)',
                    fontSize: '2.2rem',
                    fontWeight: '700'
                  }}>
                    Şifre Sıfırla
                  </Title>
                  <Text style={{ 
                    color: 'var(--text-secondary)',
                    fontSize: '1.1rem'
                  }}>
                    E-postanızdaki doğrulama kodunu ve yeni şifrenizi girin
                  </Text>
                </div>

                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: 'var(--text-primary)', 
                      fontWeight: '600', 
                      fontSize: '0.95rem',
                      marginBottom: '8px'
                    }}>
                      Doğrulama Kodu
                    </label>
                    <Input
                      prefix={<SafetyOutlined style={{ color: 'var(--primary-600)' }} />}
                      placeholder="6 haneli doğrulama kodu"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      style={{ 
                        borderRadius: '12px',
                        height: '50px',
                        fontSize: '1rem',
                        letterSpacing: '4px',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}
                    />
                    {codeError && (
                      <div style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '4px' }}>
                        {codeError}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: 'var(--text-primary)', 
                      fontWeight: '600', 
                      fontSize: '0.95rem',
                      marginBottom: '8px'
                    }}>
                      Yeni Şifre
                    </label>
                    <Input.Password
                      prefix={<LockOutlined style={{ color: 'var(--primary-600)' }} />}
                      placeholder="Yeni şifreniz"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      style={{ 
                        borderRadius: '12px',
                        height: '50px',
                        fontSize: '1rem'
                      }}
                    />
                    {passwordError && (
                      <div style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '4px' }}>
                        {passwordError}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '32px' }}>
                    <label style={{ 
                      display: 'block', 
                      color: 'var(--text-primary)', 
                      fontWeight: '600', 
                      fontSize: '0.95rem',
                      marginBottom: '8px'
                    }}>
                      Şifre Tekrar
                    </label>
                    <Input.Password
                      prefix={<LockOutlined style={{ color: 'var(--primary-600)' }} />}
                      placeholder="Yeni şifrenizi tekrar girin"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{ 
                        borderRadius: '12px',
                        height: '50px',
                        fontSize: '1rem'
                      }}
                    />
                    {confirmPasswordError && (
                      <div style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '4px' }}>
                        {confirmPasswordError}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn-primary"
                    disabled={loading}
                    onClick={handleSubmit}
                    style={{
                      width: '100%',
                      height: '52px',
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {loading ? 'Sıfırlanıyor...' : 'Şifreyi Sıfırla'}
                  </button>
                </div>
              </>
            ) : (
              <Card style={{ textAlign: 'center', borderRadius: '12px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#f0fdf4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    fontSize: '2.5rem',
                    color: '#059669'
                  }}>
                    ✓
                  </div>
                  <Title level={3} style={{ marginBottom: '16px' }}>
                    Şifre Başarıyla Sıfırlandı!
                  </Title>
                  <Text style={{ color: 'var(--text-secondary)', fontSize: '1rem' }}>
                    Yeni şifrenizle giriş yapabilirsiniz.
                  </Text>
                </div>
              </Card>
            )}

            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Link 
                to="/login" 
                style={{ 
                  color: 'var(--text-tertiary)',
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.color = 'var(--primary-600)'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.color = 'var(--text-tertiary)'}
              >
                <ArrowLeftOutlined />
                Giriş sayfasına dön
              </Link>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPassword;

